import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
    AlertTriangle,
    Bell,
    CalendarClock,
    CalendarDays,
    CheckCircle2,
    ChevronUp,
    CircleAlert,
    Copy,
    CreditCard,
    Headphones,
    CircleHelp,
    Globe,
    LayoutDashboard,
    LogOut,
    Network,
    Receipt,
    Rocket,
    Settings,
    Store,
    User,
    Users,
    Webhook,
} from 'lucide-react';
import Logo from '@/assets/logo.png';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarInset,
    SidebarMenu,
    SidebarMenuBadge,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider,
    SidebarRail,
    SidebarSeparator,
    SidebarTrigger,
    useSidebar,
} from '@/components/ui/sidebar';
import HelpSidebarProvider from '@/context/HelpSidebarContext';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import server from '@/services/server';
import { useClub } from '../ClubContext';
import ClubFeatureGate from '../components/ClubFeatureGate';
import ClubMembershipSwitcher from '../components/ClubMembershipSwitcher';
import { CLUB_NAV_GROUPS, filterClubNavGroups } from '../constants/clubNavConfig';
import { getClubTutorialLessons } from '../constants/clubTutorialLessons';
import useClubErrors from '../hooks/useClubErrors';
import { useClubTutorialProgress } from '../hooks/useClubTutorialProgress';
import {
    CLUB_SUBSCRIPTION_STATUS,
    getClubPlanHeaderStatus,
} from '../utils/clubSubscriptionStatus';

const NAV_ICONS = {
    Rocket,
    LayoutDashboard,
    Store,
    Users,
    Network,
    Settings,
    Bell,
    CreditCard,
    CircleHelp,
    Receipt,
};

const HEADER_STATUS_ICONS = {
    danger: AlertTriangle,
    warning: CalendarClock,
    caution: CalendarClock,
    notice: CalendarDays,
    info: CalendarDays,
    success: CheckCircle2,
    neutral: CircleAlert,
};

function getHeaderStatusIcon(statusInfo) {
    if (
        statusInfo?.code === CLUB_SUBSCRIPTION_STATUS.CANCELLED ||
        statusInfo?.code === CLUB_SUBSCRIPTION_STATUS.INACTIVE ||
        statusInfo?.code === CLUB_SUBSCRIPTION_STATUS.EXPIRED
    ) {
        return AlertTriangle;
    }
    if (statusInfo?.code === CLUB_SUBSCRIPTION_STATUS.WAITING) {
        return CircleAlert;
    }
    return HEADER_STATUS_ICONS[statusInfo?.tone] || CircleAlert;
}

function NavItem({
    to,
    href,
    label,
    icon: Icon,
    badge,
    badgeVariant,
    end = false,
    highlight = false,
    external = false,
}) {
    const location = useLocation();
    const isExternal = Boolean(external || href);
    const target = !isExternal ? `/clube/${to}`.replace(/\/+$/, '') : '';
    const isActive = isExternal
        ? false
        : end
          ? location.pathname === target
          : location.pathname.startsWith(target);
    const showAlertDot = badgeVariant === 'alert' && Boolean(badge);

    return (
        <SidebarMenuItem>
            <SidebarMenuButton
                render={
                    isExternal ? (
                        <a href={href} target="_blank" rel="noopener noreferrer" />
                    ) : (
                        <Link to={to} />
                    )
                }
                isActive={isActive}
                tooltip={label}
                className={highlight ? 'club-nav-item--highlight' : undefined}
            >
                <Icon />
                <span>{label}</span>
            </SidebarMenuButton>
            {showAlertDot ? (
                <SidebarMenuBadge
                    className="club-nav-alert-badge"
                    title={`${badge} item(ns) pendente(s)`}
                    aria-label={`${badge} itens de personalização pendentes`}
                >
                    <span className="club-nav-alert-dot" aria-hidden />
                </SidebarMenuBadge>
            ) : badge ? (
                <SidebarMenuBadge>{badge}</SidebarMenuBadge>
            ) : null}
        </SidebarMenuItem>
    );
}

const CLUB_ROLE_LABELS = {
    owner: 'Proprietário',
    admin: 'Administrador',
    manager: 'Gerente',
    user: 'Usuário',
    support: 'Suporte Eurobem',
};

/** Padrão oficial shadcn base-nova NavUser: Trigger render={<SidebarMenuButton />} */
function ClubNavUser({ userName, role, initials, onLogout, onRequestSupport, onLeaveSupport }) {
    const navigate = useNavigate();
    const { isMobile } = useSidebar();
    const clubPortalContainer =
        typeof document !== 'undefined' ? document.getElementById('club-root') : null;
    const roleLabel = CLUB_ROLE_LABELS[role] || role || '';
    const isSupport = role === 'support';

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                render={
                    <SidebarMenuButton
                        size="lg"
                        className="data-open:bg-sidebar-accent data-open:text-sidebar-accent-foreground"
                    />
                }
            >
                <Avatar className="size-8 rounded-lg">
                    <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{userName}</span>
                    <span className="truncate text-xs text-muted-foreground">{roleLabel}</span>
                </div>
                <ChevronUp className="ml-auto size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
                container={clubPortalContainer}
                className="min-w-56 rounded-lg"
                side={isMobile ? 'bottom' : 'right'}
                align="end"
                sideOffset={4}
            >
                <DropdownMenuItem onClick={() => navigate('/clube/apitokens')}>
                    <Webhook />
                    Integração API
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/clube/tutorial')}>
                    <Rocket />
                    Ver tutorial
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() =>
                        window.open(
                            'https://clube-eurobem.readme.io/docs/visao-geral',
                            '_blank',
                            'noopener,noreferrer',
                        )
                    }
                >
                    <CircleHelp />
                    Ajuda
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/clube/perfil')}>
                    <User />
                    Perfil
                </DropdownMenuItem>
                {!isSupport ? (
                    <DropdownMenuItem onClick={onRequestSupport}>
                        <Headphones />
                        Solicitar suporte
                    </DropdownMenuItem>
                ) : null}
                <DropdownMenuSeparator />
                {isSupport ? (
                    <DropdownMenuItem variant="destructive" onClick={onLeaveSupport || onLogout}>
                        <LogOut />
                        Deixar suporte
                    </DropdownMenuItem>
                ) : (
                    <DropdownMenuItem variant="destructive" onClick={onLogout}>
                        <LogOut />
                        Sair
                    </DropdownMenuItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default function ClubShadcnShell() {
    const navigate = useNavigate();
    const {
        club: data,
        hasResale,
        isReseller,
        hasBenefits,
        canViewFinance,
        clubCapabilities,
        personalizationPending,
        resellerPending,
        plansPending,
    } = useClub();
    const { LogOut, user } = useAuth();
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const [supportOpen, setSupportOpen] = useState(false);
    const [supportLoading, setSupportLoading] = useState(false);
    const [supportCode, setSupportCode] = useState(null);
    const [supportExpiresIn, setSupportExpiresIn] = useState(null);

    const tutorialLessons = getClubTutorialLessons({
        hasResale,
        hasPlans: isReseller || hasBenefits,
        canViewFinance,
    });
    const tutorialLessonIds = tutorialLessons.map(lesson => lesson.id);
    const { pendingCount: tutorialPendingCount } = useClubTutorialProgress(tutorialLessonIds);

    const navGroups = filterClubNavGroups(CLUB_NAV_GROUPS, clubCapabilities || {});
    const badgeValues = {
        tutorialPending: tutorialPendingCount || undefined,
        personalizationPending: personalizationPending || undefined,
        resellerPending: resellerPending || undefined,
        plansPending: plansPending || undefined,
    };

    const planStatusInfo = getClubPlanHeaderStatus(data?.Signature);
    const PlanStatusIcon = getHeaderStatusIcon(planStatusInfo);
    const clubPublicUrl = (() => {
        const raw = String(data?.domain || '').trim();
        if (!raw) return null;
        if (/^https?:\/\//i.test(raw)) return raw;
        return `https://${raw}`;
    })();
    const userInitials = (user?.userName || 'U')
        .split(' ')
        .slice(0, 2)
        .map(part => part[0])
        .join('')
        .toUpperCase();

    const handleLogout = () => {
        LogOut();
        navigate('/clube/login', { replace: true });
    };

    const handleLeaveSupport = () => {
        LogOut();
        navigate('/clube/login', { replace: true });
        window.setTimeout(() => {
            try {
                window.close();
            } catch {
                // aba pode não fechar se não foi aberta via script
            }
        }, 100);
    };

    const handleRequestSupport = async () => {
        try {
            setSupportLoading(true);
            setSupportOpen(true);
            setSupportCode(null);
            const { data: response } = await server.post('club/support/request');
            const payload = response?.data || response;
            setSupportCode(payload?.code || null);
            setSupportExpiresIn(payload?.expiresInSeconds || null);
        } catch (error) {
            setSupportOpen(false);
            setErrors(error);
        } finally {
            setSupportLoading(false);
        }
    };

    const handleCopySupportCode = async () => {
        if (!supportCode) return;
        try {
            await navigator.clipboard.writeText(String(supportCode));
            enqueueSnackbar('Código copiado.');
        } catch {
            enqueueSnackbar('Não foi possível copiar o código.', { variant: 'error' });
        }
    };

    const supportExpiresMinutes = supportExpiresIn
        ? Math.max(1, Math.round(Number(supportExpiresIn) / 60))
        : 15;

    return (
        <HelpSidebarProvider>
            <SidebarProvider>
                <Sidebar collapsible="icon" variant="inset">
                    <SidebarHeader className="border-b border-sidebar-border">
                        <div className="flex items-center gap-2 px-1 py-1">
                            <img src={Logo} alt="Eurobem" className="h-8 w-auto group-data-[collapsible=icon]:hidden" />
                            <span className="hidden font-semibold group-data-[collapsible=icon]:inline">EB</span>
                        </div>
                    </SidebarHeader>

                    <SidebarContent>
                        {navGroups.map((group, groupIndex) => (
                            <div key={group.id}>
                                {groupIndex > 0 ? <SidebarSeparator /> : null}
                                <SidebarGroup>
                                    {group.label ? <SidebarGroupLabel>{group.label}</SidebarGroupLabel> : null}
                                    <SidebarGroupContent>
                                        <SidebarMenu>
                                            {group.items.map(item => {
                                                const Icon = NAV_ICONS[item.icon] || Store;
                                                const badge =
                                                    item.badgeKey != null ? badgeValues[item.badgeKey] : undefined;
                                                return (
                                                    <NavItem
                                                        key={item.href || item.to}
                                                        to={item.to}
                                                        href={item.href}
                                                        label={item.label}
                                                        icon={Icon}
                                                        end={item.end}
                                                        highlight={item.highlight}
                                                        badge={badge}
                                                        badgeVariant={item.badgeVariant}
                                                        external={item.external}
                                                    />
                                                );
                                            })}
                                        </SidebarMenu>
                                    </SidebarGroupContent>
                                </SidebarGroup>
                            </div>
                        ))}
                    </SidebarContent>

                    <SidebarFooter className="border-t border-sidebar-border">
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <ClubNavUser
                                    userName={user?.userName?.split(' ').slice(0, 4).join(' ')}
                                    role={user?.role}
                                    initials={userInitials}
                                    onLogout={handleLogout}
                                    onRequestSupport={handleRequestSupport}
                                    onLeaveSupport={handleLeaveSupport}
                                />
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarFooter>
                    <SidebarRail />
                </Sidebar>

                <SidebarInset>
                    <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b bg-background/95 px-4 backdrop-blur supports-backdrop-filter:bg-background/80">
                        <SidebarTrigger className="-ml-1" />
                        <Separator orientation="vertical" className="mr-2 h-4" />
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                            <Link
                                to="/clube/conta"
                                className={cn('club-plan-status', `club-plan-status--${planStatusInfo.tone}`)}
                                title={planStatusInfo.message}
                            >
                                <PlanStatusIcon className="club-plan-status__icon" aria-hidden />
                                <span className="club-plan-status__text">{planStatusInfo.message}</span>
                            </Link>
                            <span className="truncate text-sm font-semibold md:hidden">{data?.name}</span>
                        </div>
                        {clubPublicUrl ? (
                            <>
                                <a
                                    href={clubPublicUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Ver meu clube"
                                    aria-label="Ver meu clube"
                                    className={cn(
                                        'inline-flex size-11 shrink-0 items-center justify-center rounded-xl border border-border bg-background outline-none transition-colors sm:hidden',
                                        'hover:bg-muted/70 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
                                    )}
                                >
                                    <Globe className="size-4" />
                                </a>
                                <a
                                    href={clubPublicUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={cn(
                                        'hidden h-11 shrink-0 items-center gap-2 rounded-xl border border-border bg-background px-3 text-sm font-medium outline-none transition-colors sm:inline-flex',
                                        'hover:bg-muted/70 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
                                    )}
                                >
                                    <Globe className="size-4 shrink-0 opacity-70" />
                                    Ver meu clube
                                </a>
                            </>
                        ) : null}
                        <ClubMembershipSwitcher />
                    </header>

                    <main className="club-layout-main flex flex-1 flex-col p-4 md:p-6">
                        <div className="club-page flex flex-1 flex-col">
                            <ClubFeatureGate>
                                <Outlet />
                            </ClubFeatureGate>
                        </div>
                    </main>
                </SidebarInset>
            </SidebarProvider>

            <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Solicitar suporte</DialogTitle>
                        <DialogDescription>
                            Informe este código ao atendimento Eurobem. Ele expira em cerca de{' '}
                            {supportExpiresMinutes} minutos e só pode ser usado uma vez.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-col items-center gap-3 py-4">
                        {supportLoading || !supportCode ? (
                            <Spinner className="size-6" />
                        ) : (
                            <>
                                <p className="font-mono text-4xl font-bold tracking-[0.35em]">{supportCode}</p>
                                <Button type="button" variant="outline" size="sm" onClick={handleCopySupportCode}>
                                    <Copy className="size-4" />
                                    Copiar código
                                </Button>
                            </>
                        )}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setSupportOpen(false)}>
                            Fechar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </HelpSidebarProvider>
    );
}
